import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { FileUpload } from 'src/app/models/FileUpload';
import { CommonFunctions } from 'src/app/Utilities/CommonFunctions';
import { environment } from 'src/environments/environment';


@Injectable({
  providedIn: 'root'
})
export class ClientcommunicationService {
  // 1. Storing the route param value, room/:id -> id value
  private pathParamState = new BehaviorSubject<string>('');
  pathParam: Observable<string>;

  private chatSocket: Socket | null = null;
  // Last-joined channels — re-emitted on every socket 'connect' (fires on the first connect AND
  // every automatic reconnect after a network drop) since Socket.IO does not persist a
  // reconnected client's room membership server-side. Without this, a transient disconnect leaves
  // the client showing connected but silently deaf to message_added/room_updated until a full
  // page refresh re-establishes the joins from scratch.
  private systemAdminChannelJoined = false;
  private currentBusinessId: string | null = null;
  private currentRoomId: string | null = null;

  constructor(private http: HttpClient) {
    this.pathParam = this.pathParamState.asObservable();
  }

  updatePathParamState(newPathParam: string): void {
    this.pathParamState.next(newPathParam);
  }

  /** Uploads one file to GridFS (via business-admin-server) and reports progress. businessId is the
   * target business's id — the same one the support conversation belongs to. */
  pushFileToStorage(fileUpload: FileUpload, businessId: string): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append('files', fileUpload.file, fileUpload.file.name);
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/anonymous/upload-attachment';
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + CommonFunctions.getTokenOfBusiness(),
      'BusinessId': businessId
    });
    return this.http.post(url, formData, { headers, reportProgress: true, observe: 'events' });
  }

  /////chatstasy server api call — System Admin's cross-business "Chat with Support" inbox, all Mongo-backed/////

  private chatApiHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: 'Bearer ' + CommonFunctions.getTokenOfBusiness(),
      'Content-Type': 'application/json',
    });
  }

  /** "Add Chat" — get-or-create the target business's support room without sending a message yet. */
  createSupportRoomRest(businessId: any): Observable<any> {
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/chat/admin/support/rooms';
    return this.http.post(url, { businessId }, { headers: this.chatApiHeaders() });
  }

  getSupportRoomsRest(): Observable<any> {
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/chat/admin/support/rooms';
    return this.http.get(url, { headers: this.chatApiHeaders() });
  }

  getSupportRoomRest(roomId: string): Observable<any> {
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/chat/admin/support/rooms/' + roomId;
    return this.http.get(url, { headers: this.chatApiHeaders() });
  }

  getSupportMessagesRest(roomId: string, size: number = 10, before: number | null = null): Observable<any> {
    let url = environment.API_URL_BUSINESS_ADMIN + '/api/chat/admin/support/rooms/' + roomId + '/messages?size=' + size;
    if (before != null) {
      url += '&before=' + before;
    }
    return this.http.get(url, { headers: this.chatApiHeaders() });
  }

  sendSupportMessageRest(roomId: string, text: string, attachments: any[]): Observable<any> {
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/chat/admin/support/rooms/' + roomId + '/messages';
    return this.http.post(url, { text, attachments }, { headers: this.chatApiHeaders() });
  }

  markSupportRoomReadRest(roomId: string): Observable<any> {
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/chat/admin/support/rooms/' + roomId + '/read';
    return this.http.post(url, {}, { headers: this.chatApiHeaders() });
  }

  /** Broadcast composer — get-or-creates every target business's support room and sends the same
   * message+attachments into each, in one call. Also records one BroadcastLog entry server-side
   * for the whole broadcast. */
  sendSupportBroadcastRest(businessIds: string[], text: string, attachments: { url: string, name: string }[]): Observable<any> {
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/chat/admin/support/broadcast';
    return this.http.post(url, { businessIds, text, attachments }, { headers: this.chatApiHeaders() });
  }

  getAdminBroadcastLogsRest(page: number = 0, size: number = 20): Observable<any> {
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/chat/admin/support/broadcastLogs?page=' + page + '&size=' + size;
    return this.http.get(url, { headers: this.chatApiHeaders() });
  }

  /** Connects (or reuses the existing connection, including one that's mid-reconnect) to the chat
   * Socket.IO server, as the fixed system-admin identity. Gating on the object's existence rather
   * than its transient `.connected` state avoids a race where two components both call this within
   * the same tick, before the first io() call finishes its handshake, spinning up a second,
   * independent socket that silently orphans the first one's listeners. */
  connectChatSocket(): Socket {
    if (this.chatSocket) {
      return this.chatSocket;
    }
    this.chatSocket = io(environment.CHAT_SOCKET_URL, {
      transports: ['websocket'],
      query: { token: CommonFunctions.getTokenOfBusiness() }
    });
    this.chatSocket.on('connect', () => {
      if (this.systemAdminChannelJoined) {
        this.chatSocket?.emit('join_system_admin', {});
      }
      if (this.currentBusinessId && this.currentRoomId) {
        this.chatSocket?.emit('join_room', { businessId: this.currentBusinessId, roomId: this.currentRoomId });
      }
    });
    return this.chatSocket;
  }

  disconnectChatSocket(): void {
    this.chatSocket?.disconnect();
    this.chatSocket = null;
    this.systemAdminChannelJoined = false;
    this.currentBusinessId = null;
    this.currentRoomId = null;
  }

  joinSystemAdminChannel(): void {
    this.systemAdminChannelJoined = true;
    this.chatSocket?.emit('join_system_admin', {});
  }

  leaveSystemAdminChannel(): void {
    this.systemAdminChannelJoined = false;
    this.chatSocket?.emit('leave_system_admin', {});
  }

  /** Fires on any support room's activity across every business. */
  onRoomUpdated(): Observable<any> {
    return new Observable((observer) => {
      const handler = (data: any) => observer.next(data);
      this.chatSocket?.on('room_updated', handler);
      return () => this.chatSocket?.off('room_updated', handler);
    });
  }

  joinRoomChannel(businessId: string, roomId: string): void {
    this.currentBusinessId = businessId;
    this.currentRoomId = roomId;
    this.chatSocket?.emit('join_room', { businessId, roomId });
  }

  leaveRoomChannel(businessId: string, roomId: string): void {
    if (this.currentRoomId === roomId) {
      this.currentRoomId = null;
    }
    this.chatSocket?.emit('leave_room', { businessId, roomId });
  }

  /** Fires when a new message is added to a room this socket has joined via joinRoomChannel(). */
  onMessageAdded(): Observable<any> {
    return new Observable((observer) => {
      const handler = (data: any) => observer.next(data);
      this.chatSocket?.on('message_added', handler);
      return () => this.chatSocket?.off('message_added', handler);
    });
  }

  /** senderType is "Business" or "System" — lets the receiver ignore its own echoed broadcast. */
  emitTyping(businessId: string, roomId: string, senderType: string, isTyping: boolean): void {
    this.chatSocket?.emit('typing', { businessId, roomId, senderType, isTyping });
  }

  /** Fires when either party's typing state changes in a room this socket has joined. */
  onTyping(): Observable<any> {
    return new Observable((observer) => {
      const handler = (data: any) => observer.next(data);
      this.chatSocket?.on('typing', handler);
      return () => this.chatSocket?.off('typing', handler);
    });
  }

  /** Fires when the other party marks a room read — payload is { roomId, senderId } where every
   * message from senderId just flipped to "read", so its sender's read-receipt icon can update live. */
  onMessagesRead(): Observable<any> {
    return new Observable((observer) => {
      const handler = (data: any) => observer.next(data);
      this.chatSocket?.on('messages_read', handler);
      return () => this.chatSocket?.off('messages_read', handler);
    });
  }

  /////////////////////////Broadcast Api's///////////////////////////////////////////

  sendSMS(smsBody: any, channel_id: any) {
    const url = environment.API_URL + '/api/anonymous/twilio/send-message/' + channel_id;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post(url, smsBody, httpOptions).pipe(timeout(60000));
  }

  sendBulkSMS(smsBody: any, clientIds: any) {
    const url = environment.API_URL + '/api/clientCommunication/bulk?clientIds=' + clientIds;
    const TOKEN = CommonFunctions.getToken();
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + TOKEN,
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post(url, smsBody, httpOptions).pipe(timeout(60000));
  }

  subscribeNotificationList() {
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/usersubscription/subscribe-notification';
    const TOKEN = CommonFunctions.getTokenOfBusiness();
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + TOKEN,
        'Content-Type': 'application/json'
      }),
    };
    return this.http.get(url, httpOptions).pipe(timeout(60000));
  }

  sendSubscriptionEmail(emailBody: string, businessId: string) {
    const url = environment.API_URL_BUSINESS_ADMIN + '/api/usersubscription/subscribe-email-notification';
    const TOKEN = CommonFunctions.getTokenOfBusiness();

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + TOKEN,
        'Content-Type': 'application/json',
        BusinessId: businessId.toString()
      }),
    };

    return this.http.get(url, { ...httpOptions, params: { emailBody } }).pipe(timeout(60000));
  }

}
